import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { A11yProvider } from "./a11y/A11yContext";
import { AuthProvider } from "./auth/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import RouteFocus from "./components/RouteFocus";
import AccessibilityStatementPage from "./pages/AccessibilityStatementPage";
import LoginCompletePage from "./pages/LoginCompletePage";
import LoginPage from "./pages/LoginPage";
import { ForgotPasswordPage, SetPasswordPage } from "./pages/PasswordPages";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ProjectsPage from "./pages/ProjectsPage";
import StudyPage from "./pages/StudyPage";
import DatasetPage from "./pages/dataset/DatasetPage";
import DbDesignPage from "./pages/dbdesign/DbDesignPage";
import ManagementPage from "./pages/admin/ManagementPage";
import AccountPage from "./pages/AccountPage";
import NotificationsPage from "./pages/NotificationsPage";
import DataTablesPage from "./pages/operate/DataTablesPage";
import DataTablePage from "./pages/operate/DataTablePage";
import ReleasePage from "./pages/ReleasePage";
import ProjectLogPage from "./pages/log/ProjectLogPage";
import { AddressProvider, useAddressLookup } from "./address/AddressContext";
import { AddressesOff, HandoffCompletePage, HandoffStartPage, ProjectHostHome,
         ProjectHostSignIn } from "./address/ProjectHostPages";

// Opt in to react-router v7 behaviour now (silences the v6 deprecation warnings).
export const ROUTER_FUTURE = { v7_startTransition: true, v7_relativeSplatPath: true };

// Every route the app has. Feature work fills the stub pages/tabs already routed
// here (DatasetPage tabs Metadata/Columns, DbDesignPage) and does not edit this file.
//
// `address` (address/AddressContext.jsx) says which host the page was opened on. On a project
// host (http://<label>.<base host>/) "/" is that project's data shell, /login sends the browser
// to the portal to sign in and /handoff takes the one-time code back; every other route is the
// same as on the portal (the API is same-origin through the same nginx).
//
// Every ProtectedRoute shows only the Profile page while the signed-in account's profile is not
// confirmed (a Keycloak self-registration, N5): components/ProtectedRoute.jsx.
export function AppRoutes({ address }) {
  const projectHost = !!address && address.kind === "project";
  if (projectHost && !address.enabled) {
    return <AddressProvider value={address}><AddressesOff /></AddressProvider>;
  }
  const hostRoutes = projectHost ? [
    <Route key="login" path="/login" element={<ProjectHostSignIn />} />,
    <Route key="handoff" path="/handoff" element={<HandoffCompletePage />} />,
    <Route key="home" path="/" element={<ProtectedRoute><ProjectHostHome /></ProtectedRoute>} />,
  ] : [
    <Route key="login" path="/login" element={<LoginPage />} />,
    <Route key="complete" path="/login/complete" element={<LoginCompletePage />} />,
    // Signed out (N5): the invitation / reset link is always {FRONTEND_URL}/set-password#token=,
    // i.e. on the portal host, so a project host has neither route.
    <Route key="forgot" path="/forgot-password" element={<ForgotPasswordPage />} />,
    <Route key="set-password" path="/set-password" element={<SetPasswordPage />} />,
    <Route key="handoff" path="/handoff" element={<ProtectedRoute><HandoffStartPage /></ProtectedRoute>} />,
    <Route key="home" path="/" element={<Navigate to="/projects" replace />} />,
  ];
  return (
    <AddressProvider value={address}>
      <RouteFocus />
      <ErrorBoundary>
        <Routes>
          {hostRoutes}
          <Route path="/accessibility" element={<AccessibilityStatementPage />} />
          <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
          <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
          <Route path="/projects/:projectId/dbdesign" element={<ProtectedRoute><DbDesignPage /></ProtectedRoute>} />
          <Route path="/projects/:projectId/log" element={<ProtectedRoute><ProjectLogPage /></ProtectedRoute>} />
          <Route path="/studies/:studyId" element={<ProtectedRoute><StudyPage /></ProtectedRoute>} />
          <Route path="/datasets/:datasetId" element={<ProtectedRoute><DatasetPage /></ProtectedRoute>} />
          <Route path="/datasets/:datasetId/:tab" element={<ProtectedRoute><DatasetPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><ManagementPage /></ProtectedRoute>} />
          <Route path="/admin/:section" element={<ProtectedRoute><ManagementPage /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          {/* Browsing a hosted project database (operate/, task 2026-10-05 O4) */}
          <Route path="/projects/:projectId/data" element={<ProtectedRoute><DataTablesPage /></ProtectedRoute>} />
          <Route path="/projects/:projectId/data/:table" element={<ProtectedRoute><DataTablePage /></ProtectedRoute>} />
          <Route path="/releases/:uuid" element={<ProtectedRoute><ReleasePage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to={projectHost ? "/" : "/projects"} replace />} />
        </Routes>
      </ErrorBoundary>
    </AddressProvider>
  );
}

export default function App() {
  const address = useAddressLookup();       // null while GET /api/address is on its way
  return (
    // Outside AuthProvider so the accessibility settings apply on the sign-in page too.
    <A11yProvider>
      <AuthProvider>
        <BrowserRouter future={ROUTER_FUTURE}>
          {address && <AppRoutes address={address} />}
        </BrowserRouter>
      </AuthProvider>
    </A11yProvider>
  );
}
